import { useAppDispatch } from './useAppDispatcher.hook';
import { getEventStatusListThunk } from '../redux/Event/Status/EventStatus.thunk';
import { EventStatus } from '../redux/Event/Status/EventStatus.type';
import { groupBy } from 'lodash';
import { useMemo, useState } from 'react';

type EventsIDsWithItemsIDs = {
  [key: number]: number[];
};

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const useStatusHook = () => {
  const dispatch = useAppDispatch();
  const [eventStatusListData, setEventStatusListData] = useState<EventStatus[]>(
    [],
  );

  const groupIdsWithItemIds = useMemo(() => {
    const eventsIDsSet = new Set<number>();
    const eventsIDsWithItemsIDsObj: EventsIDsWithItemsIDs = {};
    eventStatusListData.forEach((item: EventStatus) => {
      eventsIDsSet.add(item.event.id);
    });
    eventsIDsSet.forEach((value: number): void => {
      eventsIDsWithItemsIDsObj[value] = eventStatusListData
        .filter((item: EventStatus): boolean => value === item.event.id)
        .map((item: EventStatus): number => item.id);
    });

    return eventsIDsWithItemsIDsObj;
  }, [eventStatusListData]);

  const getGroupedOptions = (eventId: number | string, data: EventStatus[]) => {
    const options = [];

    if (eventId) {
      return data.map((item: EventStatus) => ({
        value: item.id,
        label: item.name,
        code: item.code,
        hasPlanningTime: item.hasPlanningTime,
      }));
    }

    // @ts-ignore
    const groupOptions = groupBy(data, (item: any) => {
      return item.event.name;
    });

    for (const option in groupOptions) {
      const value = groupOptions[option];

      options.push({
        label: option,
        options: value.map((item: EventStatus) => ({
          value: item.id,
          label: item.name,
          code: item.code,
          hasPlanningTime: item.hasPlanningTime,
        })),
      });
    }

    return options;
  };

  const getSelectList = async (
    search: string,
    loadedOptions: string | any[],
    { eventId, page }: any,
  ) => {
    const {
      payload: { data, pagination },
    } = await dispatch(getEventStatusListThunk({ eventId, search, page }));

    const options = getGroupedOptions(eventId, data);

    return {
      options,
      hasMore: !eventId
        ? pagination.current_page < pagination.last_page
        : false,
      additional: {
        eventId,
        page: pagination.current_page + 1,
      },
    };
  };

  const getSelectWholeList = async (
    search: string,
    loadedOptions: string | any[],
    { eventId, page }: any,
  ) => {
    const {
      payload: { data, pagination },
    } = await dispatch(getEventStatusListThunk({ eventId, search, page }));

    let allData = [...data];
    let currentPage = pagination.current_page;
    let lastPage = pagination.last_page;
    while (currentPage < lastPage) {
      const { payload } = await dispatch(
        getEventStatusListThunk({ eventId, search, page: currentPage + 1 }),
      );
      allData = [...allData, ...payload.data];
      setEventStatusListData(allData);
      currentPage = payload.pagination.current_page;
    }

    const options = getGroupedOptions(eventId, allData).filter(
      option => option?.label !== 'Групповая стажировка',
    );

    return {
      options,
      hasMore: false,
      additional: {
        eventId,
        page: pagination.current_page + 1,
      },
    };
  };

  const getSelectListByEventCode = async (
    search: string,
    loadedOptions: string | any[],
    { eventId, page }: any,
  ) => {
    return await getSelectWholeList(search, loadedOptions, {
      eventId,
      page,
    });
  };

  const getSelectListByIds = async (ids: Array<number>) => {
    const {
      payload: { data },
    } = await dispatch(getEventStatusListThunk({ ids }));

    return data?.map((item: EventStatus) => ({
      value: item.id,
      label: item.name,
    }));
  };

  const getGroupIdByItemId = (itemId: number) => {
    return Object.entries(groupIdsWithItemIds)
      .find(([key, value]) => value.includes(itemId))
      ?.at(0);
  };

  return {
    getSelectList,
    getSelectWholeList,
    getSelectListByIds,
    getGroupIdByItemId,
    getSelectListByEventCode,
  };
};

export default useStatusHook;
